import { AlarmClock, TimerOff } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { nowMs, toEpochMs, type Clock } from '@/lib/clock';
import { describeDuration, formatCountdown } from '@/lib/datetime';
import { cn } from '@/lib/utils';

/**
 * A live countdown to a deadline.
 *
 * This is the component the SAD's seven timers surface through: reservation TTL, evidence
 * deadline, verification max, credit-pending retry, review SLA, late-slip grace, withdrawal
 * max-age. Three things make it fit for that job rather than merely decorative:
 *
 * - **It takes a `clock`.** Time is injected, not read from `Date.now()` inside the render, so
 *   tests drive it with fake timers and a fixed clock rather than sleeping (R5).
 * - **It escalates.** Comfortable → warning at 25% remaining → critical under 60 s → expired.
 *   Colour changes *and* the icon changes, because a depositor with 40 seconds left needs to
 *   notice without reading.
 * - **It is announced politely.** The visible digits tick every second, which would flood a
 *   screen reader; instead the ticking text is `aria-hidden` and a `role="timer"` element
 *   announces the remaining time in words on a much coarser cadence.
 */

/** Props for {@link CountdownTimer}. */
export interface CountdownTimerProps {
  /** ISO-8601 deadline. */
  deadline: string;
  /** The full window in milliseconds, used to compute the warning threshold. */
  totalMs?: number | undefined;
  /** Label read before the remaining time, e.g. "Reservation expires in". */
  label?: string | undefined;
  /** Called once when the deadline passes while mounted. */
  onExpire?: () => void;
  /** Visual size. */
  size?: 'sm' | 'md' | 'lg' | undefined;
  /** Injected clock. Defaults to the system clock. */
  clock?: Pick<Clock, 'nowMs'> | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** Urgency bucket a remaining duration falls into. */
export type CountdownUrgency = 'calm' | 'warning' | 'critical' | 'expired';

/** Classify how urgent a countdown is. Exported so tables can sort or filter by it. */
export function countdownUrgency(
  remainingMs: number,
  totalMs: number | undefined,
): CountdownUrgency {
  if (remainingMs <= 0) return 'expired';
  if (remainingMs <= 60_000) return 'critical';
  if (totalMs !== undefined && totalMs > 0 && remainingMs / totalMs <= 0.25) return 'warning';
  if (totalMs === undefined && remainingMs <= 5 * 60_000) return 'warning';
  return 'calm';
}

const URGENCY_CLASSES: Record<CountdownUrgency, string> = {
  calm: 'text-foreground',
  warning: 'text-state-attention',
  critical: 'text-state-bad',
  expired: 'text-muted-foreground',
};

const SIZE_CLASSES = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-2xl font-semibold',
} as const;

/** A ticking countdown to `deadline`. */
export function CountdownTimer({
  deadline,
  totalMs,
  label,
  onExpire,
  size = 'md',
  clock,
  className,
}: CountdownTimerProps): ReactNode {
  const { t } = useTranslation();
  const read = clock?.nowMs ?? nowMs;
  const [remaining, setRemaining] = useState(() => toEpochMs(deadline) - read());

  useEffect(() => {
    const target = toEpochMs(deadline);
    const tick = (): void => {
      setRemaining(target - read());
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => {
      clearInterval(id);
    };
  }, [deadline, read]);

  const expired = remaining <= 0;

  useEffect(() => {
    if (expired && onExpire) onExpire();
  }, [expired, onExpire]);

  const urgency = countdownUrgency(remaining, totalMs);
  const spoken = expired
    ? t('domain.countdownTimer.deadlinePassed')
    : t('domain.countdownTimer.remaining', { duration: describeDuration(remaining) });

  return (
    <span
      className={cn(
        'tnum inline-flex items-center gap-1.5 font-medium',
        SIZE_CLASSES[size],
        URGENCY_CLASSES[urgency],
        className,
      )}
      data-urgency={urgency}
    >
      {expired ? (
        <TimerOff className="size-4 shrink-0" aria-hidden="true" />
      ) : (
        <AlarmClock
          className={cn('size-4 shrink-0', urgency === 'critical' && 'animate-pulse')}
          aria-hidden="true"
        />
      )}
      {label ? <span className="font-normal text-muted-foreground">{label}</span> : null}
      <span aria-hidden="true">
        {expired ? t('domain.countdownTimer.expiredLabel') : formatCountdown(remaining)}
      </span>
      <span role="timer" aria-live="polite" aria-atomic="true" className="sr-only">
        {label ? `${label} ` : ''}
        {spoken}
      </span>
    </span>
  );
}

/**
 * A countdown to an SLA deadline that keeps counting *past* zero.
 *
 * A breached review SLA is more interesting than an expired one — "4h 12m over" is the number an
 * operations lead needs, and hiding it behind "expired" would lose the severity ordering the
 * queue sorts by.
 */
export function SlaCountdown({
  dueAt,
  clock,
  className,
}: {
  /** ISO-8601 SLA deadline. */
  dueAt: string;
  /** Injected clock. */
  clock?: Pick<Clock, 'nowMs'> | undefined;
  /** Additional classes. */
  className?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const read = clock?.nowMs ?? nowMs;
  const [remaining, setRemaining] = useState(() => toEpochMs(dueAt) - read());

  useEffect(() => {
    const target = toEpochMs(dueAt);
    const id = setInterval(() => {
      setRemaining(target - read());
    }, 1_000);
    setRemaining(target - read());
    return () => {
      clearInterval(id);
    };
  }, [dueAt, read]);

  const breached = remaining <= 0;
  return (
    <span
      className={cn(
        'tnum inline-flex items-center gap-1.5 text-xs font-medium',
        breached
          ? 'text-state-bad'
          : remaining < 3_600_000
            ? 'text-state-attention'
            : 'text-muted-foreground',
        className,
      )}
      data-breached={breached}
    >
      {breached ? (
        <TimerOff className="size-3.5" aria-hidden="true" />
      ) : (
        <AlarmClock className="size-3.5" aria-hidden="true" />
      )}
      <span aria-hidden="true">
        {breached
          ? t('domain.countdownTimer.over', { time: formatCountdown(Math.abs(remaining)) })
          : t('domain.countdownTimer.left', { time: formatCountdown(remaining) })}
      </span>
      <span className="sr-only">
        {breached
          ? t('domain.countdownTimer.slaBreachedBy', {
              duration: describeDuration(Math.abs(remaining)),
            })
          : t('domain.countdownTimer.untilSlaBreached', {
              duration: describeDuration(remaining),
            })}
      </span>
    </span>
  );
}
